import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  Shield,
  Fingerprint,
  Lock,
  AlertCircle,
  Eye,
  EyeOff,
} from 'lucide-react';
import { toast } from 'sonner';
import { biometricService } from '../utils/security/biometric';
import { pinCodeService } from '../utils/security/pinCode';
import { authService } from '../utils/auth/authService';

interface LockScreenProps {
  userId: string;
  userName: string;
  onUnlock: () => void;
}

export default function LockScreen({ userId, userName, onUnlock }: LockScreenProps) {
  const [pin, setPin] = useState('');
  const [showPin, setShowPin] = useState(false);
  const [biometricAvailable, setBiometricAvailable] = useState(false);
  const [biometricType, setBiometricType] = useState('Biometric');
  const [pinInfo, setPinInfo] = useState<any>(null);
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [savedUser, setSavedUser] = useState<any>(null);

  useEffect(() => {
    // Get saved user from localStorage
    const authSessionKey = 'auth_session';
    const savedUserData = localStorage.getItem(authSessionKey);
    if (savedUserData) {
      try {
        const userData = JSON.parse(savedUserData);
        setSavedUser(userData);
      } catch (error) {
        console.error('Error parsing saved user:', error);
      }
    }

    checkAuthMethods();
    
    // Update time every second
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(interval);
  }, [userId]);

  const checkAuthMethods = async () => {
    // Use saved user's security settings if available
    const userToCheck = savedUser || { id: userId, security: {} };
    
    // Check biometric
    const available = await biometricService.isAvailable();
    // Only show if available on device AND enabled in profile AND we have local credentials
    const hasBiometric = userToCheck.security?.biometricEnabled && biometricService.hasCredential(userId);
    setBiometricAvailable(available && hasBiometric);
    
    if (available) {
      const type = await biometricService.getBiometricType();
      setBiometricType(type);
    }

    // Check PIN - use saved user's PIN settings
    let info = pinCodeService.getPinInfo(userId);
    if (!info.enabled && userToCheck.security?.pinEnabled) {
      // Use saved user's PIN settings
      info = {
        enabled: true,
        attempts: 0,
        maxAttempts: 5,
        isLocked: false,
        lockoutMinutes: 0
      };
    }
    setPinInfo(info);

    // Auto-trigger biometric if available and configured
    if (available && hasBiometric) {
      setTimeout(() => {
        handleBiometricAuth();
      }, 500);
    }
  };

  const handleBiometricAuth = async () => {
    setIsAuthenticating(true);

    try {
      // The new authenticate method doesn't need userId, it retrieves stored credentials
      const result = await biometricService.authenticate();
      
      if (result.success) {
        // Optional: Verify the retrieved username matches the current user
        if (result.credentials?.username && result.credentials.username !== savedUser?.email && result.credentials.username !== userId) {
           // If the biometric user doesn't match the locked user, we might want to warn or re-login
           // But for now, if they passed biometric, we unlock.
        }
        
        toast.success('Authentication successful');
        onUnlock();
      } else {
        toast.error(result.error || 'Authentication failed');
      }
    } catch (error) {
      console.error('Biometric authentication error:', error);
      toast.error('Authentication failed');
    } finally {
      setIsAuthenticating(false);
    }
  };

  const handlePinSubmit = async () => {
    if (pin.length < 4) {
      toast.error('Please enter your PIN');
      return;
    }

    setIsAuthenticating(true);

    // Try verifying with saved user's PIN first (from session)
    let result: { success: boolean; error?: string; attemptsRemaining?: number } = { success: false, error: 'Incorrect PIN' };
    const savedPin = savedUser?.security?.pin || savedUser?.pin;
    if (savedPin) {
      if (pin === savedPin) {
        result = { success: true, error: '' };
      }
    } else {
      // Fallback to authService verifyPin (uses session)
      const user = await authService.verifyPin(pin);
      result = user ? { success: true } : { success: false, error: 'Incorrect PIN' };
    }
    
    setIsAuthenticating(false);

    if (result.success) {
      toast.success('PIN verified');
      onUnlock();
    } else {
      toast.error(result.error || 'Incorrect PIN');
      setPin('');
      
      // Update PIN info (attempt counters) for UX if using pinCodeService
      setPinInfo(pinCodeService.getPinInfo(userId));
    }
  };

  const handlePinInput = (value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 6);
    setPin(digits);
    
    const savedPin = savedUser?.security?.pin || savedUser?.pin;
    
    // Smart auto-submit: only if length matches stored PIN
    if (savedPin) {
      if (digits.length === savedPin.length) {
        setTimeout(() => {
          handlePinSubmit();
        }, 300);
      }
    } else {
      // Fallback: only auto-submit at max length (6) to avoid blocking 6-digit PINs
      if (digits.length === 6) {
        setTimeout(() => {
          handlePinSubmit();
        }, 300);
      }
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handlePinSubmit();
    }
  };

  const formatTime = (date: Date): string => {
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  const formatDate = (date: Date): string => {
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <div className="fixed inset-0 z-[100] bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 flex flex-col items-center justify-center p-4">
      {/* Time Display */}
      <div className="text-center text-white mb-12">
        <h1 className="text-7xl md:text-8xl font-light mb-2">
          {formatTime(currentTime)}
        </h1>
        <p className="text-xl md:text-2xl text-white/80">
          {formatDate(currentTime)}
        </p>
      </div>

      {/* Lock Icon & App Info */}
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center w-24 h-24 bg-white/20 backdrop-blur-sm rounded-full mb-4 p-3">
          <img src="/oilwellx-logo.png" alt="OilWellX" className="w-full h-full object-contain" />
        </div>
        <h2 className="text-2xl font-semibold text-white mb-1">
          OilWellX
        </h2>
        <p className="text-white/80">
          {savedUser?.name || userName || 'No saved user found'}
        </p>
        {savedUser?.company && (
          <p className="text-white/60 text-sm mt-1">
            {savedUser.company}
          </p>
        )}
      </div>

      {/* Authentication Methods */}
      <div className="w-full max-w-sm space-y-4">
        {/* Biometric */}
        {biometricAvailable && (
          <button
            onClick={handleBiometricAuth}
            disabled={isAuthenticating}
            className="w-full py-4 px-6 bg-white/20 backdrop-blur-md hover:bg-white/30 active:bg-white/40 rounded-2xl transition-all shadow-lg"
          >
            <div className="flex flex-col items-center space-y-2">
              <Fingerprint className="h-12 w-12 text-white" />
              <span className="text-white font-medium">
                Unlock with {biometricType}
              </span>
            </div>
          </button>
        )}

        {/* PIN */}
        {pinInfo?.enabled && !pinInfo.isLocked && (
          <div className="bg-white/20 backdrop-blur-md rounded-2xl p-6 shadow-lg">
            <div className="flex items-center justify-center space-x-2 mb-4">
              <Lock className="h-5 w-5 text-white" />
              <span className="text-white font-medium">Enter PIN</span>
            </div>

            <div className="relative">
              <Input
                type={showPin ? 'text' : 'password'}
                value={pin}
                onChange={(e) => handlePinInput(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="••••"
                className="bg-white/20 border-white/30 text-white placeholder-white/50 text-2xl tracking-widest text-center pr-12 h-14"
                maxLength={6}
                inputMode="numeric"
                autoFocus
                disabled={isAuthenticating}
              />
              <button
                onClick={() => setShowPin(!showPin)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-white/70 hover:text-white"
              >
                {showPin ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>

            {pinInfo.attempts > 0 && (
              <div className="mt-3 flex items-center space-x-2 text-orange-300 text-sm">
                <AlertCircle className="h-4 w-4" />
                <span>
                  {pinInfo.maxAttempts - pinInfo.attempts} {pinInfo.maxAttempts - pinInfo.attempts === 1 ? 'attempt' : 'attempts'} remaining
                </span>
              </div>
            )}
          </div>
        )}

        {/* Locked State */}
        {pinInfo?.isLocked && (
          <div className="bg-red-500/20 backdrop-blur-md rounded-2xl p-6 shadow-lg border-2 border-red-400/50">
            <div className="flex items-start space-x-3">
              <AlertCircle className="h-6 w-6 text-red-300 flex-shrink-0 mt-0.5" />
              <div className="text-white">
                <p className="font-semibold mb-1">Account Temporarily Locked</p>
                <p className="text-sm text-white/80">
                  Too many failed attempts. Please try again in {pinInfo.lockoutMinutes} {pinInfo.lockoutMinutes === 1 ? 'minute' : 'minutes'}.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* No Auth Methods */}
        {!biometricAvailable && !pinInfo?.enabled && (
          <div className="bg-white/20 backdrop-blur-md rounded-2xl p-6 shadow-lg text-center">
            <Shield className="h-12 w-12 text-white mx-auto mb-3" />
            <p className="text-white font-medium mb-2">
              No Security Method Set Up
            </p>
            <p className="text-white/80 text-sm">
              Please set up biometric or PIN authentication in settings
            </p>
          </div>
        )}
      </div>

      {/* Footer Hint */}
      {!pinInfo?.isLocked && (
        <div className="absolute bottom-8 text-center text-white/60 text-sm">
          {biometricAvailable && (
            <p>Use {biometricType} or PIN to unlock</p>
          )}
          {!biometricAvailable && pinInfo?.enabled && (
            <p>Enter your PIN to unlock</p>
          )}
        </div>
      )}
    </div>
  );
}
